import {
  BannerType,
  setDisabledSalesBanner,
  setShowOnboardingModal,
  useAppDispatch,
  useAppSelector,
  useCms,
  useUserInfo,
} from "@next-platform/client-common-core";
import { Onboarding } from "@src/components/Onboarding";
import { ReactNode, useEffect } from "react";
import { matchPath, Outlet, useNavigate, useSearchParams } from "react-router-dom";

import { AccountRequiredModal, CmsBanner, CmsBannerService } from "@/components";
import { Container } from "@/components/customComponents";
import Footer from "@/components/footer/Footer";
import MinimalFooter from "@/components/footer/MinimalFooter";
import PartnerFooter from "@/components/PartnerFooter";
import Trans from "@/components/Trans/Trans";
import UnsupportedBrowserBanner from "@/components/UnsupportedBrowserBanner/UnsupportedBrowserBanner";
import WaitingQueue from "@/components/WaitingQueue/WaitingQueue";
import {
  LEGAL_PAGES,
  PREVIEW_BANNER_KEY,
  PRIVATE_APPLE_ID,
  ROUTES,
  ROUTES_UNAVAILABLE_FOR_PIXELCHAT,
  ROUTES_UNAVAILABLE_FOR_SECRETMATE,
  SHOULD_REDIRECT_USER_LOCAL_STORAGE_KEY,
  SHOULD_SHOW_FTUE_MODAL_LOCAL_STORAGE_KEY,
} from "@/constants";
import { useBrand, useLoginEvent, useMediaStore } from "@/hooks";
import useLocalizedLocation from "@/hooks/useLocalizedLocation";
import { useSyncVisualViewportHeight } from "@/hooks/useSyncVisualViewportHeight";
import NotFoundPage from "@/pages/NotFound/NotFound";
import { MediaStore } from "@/providers/MediaProvider";
import useAuth from "@/services/useAuth";
import { cn, isSaleLive } from "@/utils";
import { getIsPartner } from "@/utils/getIsPartner";

import { useMinimalChrome } from "./MinimalChromeContext";

const IMPERSONATE_LOCAL_STORAGE_VALUE = localStorage.getItem("impersonate");

interface IAppLayoutProps {
  hideFooter?: boolean;
  isProtected?: boolean;
}

// TODO(karlbernard): [NX-11136] AppLayout is a layout orchestrator; split into sub-hooks/components to reduce size and complexity
// eslint-disable-next-line max-lines-per-function, complexity -- a layout orchestrator until the split in the TODO above
const AppLayout = ({ hideFooter = false, isProtected = false }: IAppLayoutProps) => {
  const { isAuthenticated, user, getToken, permissions } = useAuth();
  const { isPixelChat, isSecretMate } = useBrand();
  const [urlSearchParams] = useSearchParams();
  const previewBannerName = urlSearchParams.get(PREVIEW_BANNER_KEY) || undefined;
  useCms(previewBannerName);
  const isMobile = useMediaStore((store: MediaStore) => store.isMobile);
  const isPartner = getIsPartner();
  const { onlyUserId, isInvalid, userProfile: profile, isProfileLoaded } = useUserInfo();
  const { pagePath, search, isSupportedLang } = useLocalizedLocation();
  const hasSearchParams = search.length > 0;
  const navigate = useNavigate();
  const isHomePage = pagePath === "/";
  const isChatPage = pagePath?.split("/")[1] === "chat";
  const isChatStoryPage = pagePath?.split("/")[1] === "story";
  const isCreaterPage = pagePath?.startsWith("/creator");
  const isSalePage = pagePath === ROUTES.sale;
  const isCreateGroupPage = pagePath.startsWith(ROUTES.groupchatCreate);
  const isMyGroupsPage = pagePath.startsWith(ROUTES.groupchats);
  const isMyLorebooksPage = pagePath.startsWith(ROUTES.myCreationLorebook);
  const isCreateCharacterPage = pagePath === ROUTES.createChatbot;
  const isLorebookCreatePage = pagePath === ROUTES.lorebookCreate;
  const isMyCharactersPage = pagePath === ROUTES.chatbots;
  const isPersonasPage = pagePath === ROUTES.personas;
  const isVoicePage = pagePath === ROUTES.voiceCreate;
  const isMyVoicesPage = pagePath === ROUTES.voices;
  const isSubscribePage = pagePath === ROUTES.subscribe;
  const isFavoriteBotsPage = pagePath === ROUTES.favoriteBots;
  const isFavoritesLorebooksPage = pagePath === ROUTES.favoritesLorebooks;
  const isFavoritesGroupsPage = pagePath === ROUTES.favoritesGroups;
  // The Gallery pages open the sign in overlay for a logged out reader instead.
  const isMyGalleryPage = pagePath === ROUTES.myGallery;
  const isMediaFilePage = matchPath(ROUTES.mediaFile, pagePath) !== null;
  const dispatch = useAppDispatch();
  const isV2 =
    isHomePage ||
    pagePath === ROUTES.recommendedBots ||
    pagePath === ROUTES.favoriteBots ||
    pagePath === ROUTES.favoritesLorebooks ||
    pagePath === ROUTES.favoritesGroups ||
    isCreaterPage;

  const isMinimalHeaderActive = useMinimalChrome();
  CmsBannerService.setPathname(pagePath);
  const isAppleIdPrivate = profile?.email?.split("@")[1] === PRIVATE_APPLE_ID;

  const shouldShowOnboardingModal = useAppSelector((state) => state.navigation.showOnboardingModal);

  // Determine the pages that have versions for anonymous users
  const isAuthRequiredModal = !(
    isCreateCharacterPage ||
    isLorebookCreatePage ||
    isFavoriteBotsPage ||
    isFavoritesLorebooksPage ||
    isFavoritesGroupsPage ||
    isMyCharactersPage ||
    isMyGroupsPage ||
    isMyLorebooksPage ||
    isVoicePage ||
    isMyVoicesPage ||
    isSubscribePage ||
    isPersonasPage ||
    isCreateGroupPage ||
    isMyGalleryPage ||
    isMediaFilePage
  );
  useLoginEvent();
  const isSaleOrV2 = isSalePage || isV2;
  const isChatOrStory = isChatPage || isChatStoryPage;
  const isMobileChatShell = isChatOrStory && isMobile;
  useSyncVisualViewportHeight(isMobileChatShell);

  // Check if we need to navigate to the signup page (profile incomplete )
  // check if the user is invalid
  // if the email needs to be updated (apple usecase)
  useEffect(() => {
    if (isInvalid) {
      navigate(ROUTES.userNotFound);
    }

    const handleShouldRedirectUser = () => {
      if (!isHomePage) {
        if (localStorage.getItem(SHOULD_REDIRECT_USER_LOCAL_STORAGE_KEY) === null) {
          navigate(ROUTES.home);
        } else localStorage.removeItem(SHOULD_REDIRECT_USER_LOCAL_STORAGE_KEY);
      }
    };

    const handleShowOnboardingModal = () => {
      dispatch(setShowOnboardingModal(true));
    };

    if (isAuthenticated && isProfileLoaded && !profile?.username && !isInvalid && !LEGAL_PAGES.includes(pagePath)) {
      if (isPartner) {
        return navigate(ROUTES.externalSignup);
      }

      handleShouldRedirectUser();

      handleShowOnboardingModal();
    }

    if (isAppleIdPrivate && isProfileLoaded && profile?.username && pagePath !== ROUTES.updateEmail) {
      navigate(ROUTES.updateEmail);
    }
  }, [
    isAuthenticated,
    user,
    pagePath,
    isHomePage,
    navigate,
    getToken,
    dispatch,
    profile,
    isProfileLoaded,
    permissions,
    isInvalid,
    isAppleIdPrivate,
    isPartner,
  ]);

  //By default disable the sales banner override
  useEffect(() => {
    dispatch(setDisabledSalesBanner(false));
  }, [dispatch]);
  useEffect(() => {
    if (isPixelChat && (ROUTES_UNAVAILABLE_FOR_PIXELCHAT as readonly string[]).includes(pagePath)) {
      navigate("*");
    }
    if (isSecretMate && (ROUTES_UNAVAILABLE_FOR_SECRETMATE as readonly string[]).includes(pagePath)) {
      navigate("*");
    }
  }, [pagePath, isPixelChat, isSecretMate, navigate]);

  useEffect(() => {
    if (hasSearchParams) {
      localStorage.setItem(SHOULD_SHOW_FTUE_MODAL_LOCAL_STORAGE_KEY, "false");
    }
    return () => {
      localStorage.setItem(SHOULD_SHOW_FTUE_MODAL_LOCAL_STORAGE_KEY, "false");
    };
  }, [pagePath, hasSearchParams]);

  let footerElement: ReactNode = <Footer />;
  if (isMinimalHeaderActive) {
    footerElement = <MinimalFooter />;
  } else if (isPartner) {
    footerElement = <PartnerFooter />;
  }

  return (
    <>
      <div className="grow flex flex-col relative">
        <div
          className={cn(
            "flex flex-col grow",
            isSaleLive() && isSalePage && "bg-[linear-gradient(to_bottom,#071939_50%,#301950_100%)]",
            isMobile && "px-0",
            hideFooter ? "grow" : "min-h-screen",
          )}
        >
          <UnsupportedBrowserBanner />
          {IMPERSONATE_LOCAL_STORAGE_VALUE ? (
            <div className="bg-[red] text-white p-2 flex justify-center">
              <Trans i18nKey="common:impersonateModeBanner" />
            </div>
          ) : null}
          <WaitingQueue />

          <Container
            className={cn(
              "px-3 mob:px-4 mob:pt-4",
              isSaleOrV2 && "px-0 mob:px-0",
              isSaleOrV2 && isHomePage && "mob:pt-0",
              isChatOrStory && "p-0 mob:p-0",
            )}
          >
            <CmsBanner
              placement={BannerType.Top}
              shouldBeExcludedFromPagesWithTags={true}
              className={"mb-2"}
              isVisibleOnPartner={false}
              isLayoutBanner={true}
            />
            <CmsBanner placement={BannerType.PopUp} isVisibleOnPartner={false} />

            {isSupportedLang ? <Outlet /> : <NotFoundPage />}
            {!isPartner && isProtected && !onlyUserId && isAuthRequiredModal ? <AccountRequiredModal /> : null}
            {shouldShowOnboardingModal ? <Onboarding /> : null}
          </Container>
        </div>
        {!hideFooter ? footerElement : null}
      </div>
    </>
  );
};

export default AppLayout;
